import { calculateCoefficient } from './calculateCoefficient';
import getViewsBase from './get-views-base';

const INFINITY_VALUE = 1000000000;
const isInfinity = (value) => value === null || value === INFINITY_VALUE;

export const sumExtraPoints = (items) =>
  (items || []).reduce((sum, item) => sum + (Number(item?.value) || 0), 0);

/** Берёт самую позднюю archived-запись по taskClosedAt. */
export const getLatestArchivedRef = (archivedModelsOrRefs) => {
  if (!Array.isArray(archivedModelsOrRefs) || archivedModelsOrRefs.length === 0) {
    return null;
  }

  const latest = archivedModelsOrRefs.reduce((acc, item) => {
    const ref = item?.ref || item;
    const accRef = acc?.ref || acc;
    const a = accRef?.taskClosedAt ? new Date(accRef.taskClosedAt).getTime() : -Infinity;
    const b = ref?.taskClosedAt ? new Date(ref.taskClosedAt).getTime() : -Infinity;
    return b > a ? item : acc;
  }, null);

  return latest?.ref || latest || null;
};

/**
 * Живой коэффициент по текущему плану просмотров и damping.
 * Без плана / типа / damping → 1; без подходящего диапазона → 0.
 */
export const resolveLiveCoefficient = ({
  contentCardDefault,
  cardPointData,
  contentViewPlan,
  dampingCoefficients,
}) => {
  if (!contentViewPlan) {
    return 1;
  }

  if (!contentCardDefault || !Array.isArray(dampingCoefficients) || dampingCoefficients.length === 0) {
    return 1;
  }

  if (!cardPointData) {
    return 1;
  }

  const viewsBase = getViewsBase(contentViewPlan, contentCardDefault.type);
  const viewsReal = cardPointData.views ?? 0;

  let percent;
  if (viewsBase <= 0) {
    percent = viewsReal === 0 ? 0 : Infinity;
  } else {
    percent = ((viewsReal - viewsBase) / viewsBase) * 100;
  }

  let matchingCoefficient = null;
  for (const dc of dampingCoefficients) {
    const from = dc.from;
    const to = dc.to;
    const effectiveFrom = from === INFINITY_VALUE ? -Infinity : from !== null ? from : -100;
    const matchesFrom = from === INFINITY_VALUE ? true : percent >= effectiveFrom;
    const matchesTo = isInfinity(to) || percent <= to;

    if (matchesFrom && matchesTo) {
      matchingCoefficient = dc;
      break;
    }
  }

  if (!matchingCoefficient) {
    return 0;
  }

  return calculateCoefficient({
    contentCardDefault,
    cardPointData,
    contentViewPlan,
    dampingCoefficients,
  });
};

const buildArchivedContentCardDefault = (archive, contentCardDefault) => {
  const basePoints = Number(archive.typeValue) || 0;

  if (contentCardDefault) {
    return {
      ...contentCardDefault,
      type: contentCardDefault.type || archive.type || '',
      name: contentCardDefault.name || archive.typeTitle || '',
      value:
        contentCardDefault.value != null && contentCardDefault.value !== ''
          ? contentCardDefault.value
          : basePoints,
    };
  }

  return {
    type: archive.type || '',
    name: archive.typeTitle || '',
    value: basePoints,
  };
};

/**
 * Баллы карточки для бейджа карточки и суммы списка.
 *
 * Закрытая задача с архивом: typeValue / coefficient / typeTitle из снимка закрытия,
 * доп. баллы — текущие (их нет в архиве).
 * Иначе: live ContentCardDefault + пересчёт коэффициента.
 */
export default function computeCardPoints({
  cardPointData,
  contentCardDefault,
  cardPointAdvancedData,
  archivedData,
  contentViewPlan,
  dampingCoefficients,
}) {
  const extraPoints = sumExtraPoints(cardPointAdvancedData);
  const archive = archivedData?.ref || archivedData || null;
  const isClosed = cardPointData?.closed === true;

  if (isClosed && archive) {
    const basePoints = Number(archive.typeValue) || 0;
    const coefficientNum = Number(archive.coefficient);
    const coefficient = Number.isFinite(coefficientNum) ? coefficientNum : 1;

    return {
      contentCardDefault: buildArchivedContentCardDefault(archive, contentCardDefault),
      cardPointAdvancedData: cardPointAdvancedData || [],
      basePoints,
      extraPoints,
      coefficient,
      totalPoints: (basePoints + extraPoints) * coefficient,
      fromArchive: true,
    };
  }

  const basePoints =
    contentCardDefault && contentCardDefault.value != null && contentCardDefault.value !== ''
      ? Number(contentCardDefault.value) || 0
      : 0;
  const coefficient = resolveLiveCoefficient({
    contentCardDefault,
    cardPointData,
    contentViewPlan,
    dampingCoefficients,
  });

  return {
    contentCardDefault: contentCardDefault || null,
    cardPointAdvancedData: cardPointAdvancedData || [],
    basePoints,
    extraPoints,
    coefficient,
    totalPoints: (basePoints + extraPoints) * coefficient,
    fromArchive: false,
  };
}
